import { useState } from "react"

import { format, subDays } from "date-fns"

import Icon from "@/components/Icon"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Calendar as CalendarComponent } from "@/components/ui/calendar"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { OVERVIEW_SHERIFF_TYPES } from "@/constants/sheriffOptions"
import { useDriverPerformanceIssues } from "@/hooks/useDriverPerformanceIssues"
import { cn } from "@/lib/utils"
import { formatPersonShortName } from "@/utils/personDisplay"

interface DriverPerformanceTableProps {
  selectedLocation: string | null
}

export const DriverPerformanceTable = ({ selectedLocation }: DriverPerformanceTableProps) => {
  const [startDate, setStartDate] = useState<Date>(() => subDays(new Date(), 30))
  const [endDate, setEndDate] = useState<Date>(() => new Date())
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [sheriffType, setSheriffType] = useState<string | null>(null)

  const { data, isLoading, error } = useDriverPerformanceIssues({
    startDate: format(startDate, "yyyy-MM-dd"),
    endDate: format(endDate, "yyyy-MM-dd"),
    location: selectedLocation || undefined,
    sheriffTypes: sheriffType ? [sheriffType] : OVERVIEW_SHERIFF_TYPES,
  })

  const handlePersonClick = (personId: string | null) => {
    if (!personId) {
      return
    }
    const url = `/people?personId=${encodeURIComponent(personId)}&tab=people`
    window.open(url, "_blank")
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <CardTitle className="text-xl">Driver Service Issues</CardTitle>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium whitespace-nowrap">Sheriff Type</label>
            <Select
              value={sheriffType || "all"}
              onValueChange={(value) => setSheriffType(value === "all" ? null : value)}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {OVERVIEW_SHERIFF_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="flex items-center gap-2">
                  <Icon name="calendarBlank" className="h-4 w-4" />
                  {format(startDate, "MMM dd, yyyy")} - {format(endDate, "MMM dd, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="p-4">
                  <div className="flex gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Start Date</label>
                      <CalendarComponent
                        mode="single"
                        selected={startDate}
                        disabled={{ after: new Date() }}
                        onSelect={(date) => {
                          if (date && date <= endDate) {
                            setStartDate(date)
                          }
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">End Date</label>
                      <CalendarComponent
                        mode="single"
                        selected={endDate}
                        disabled={{ after: new Date() }}
                        onSelect={(date) => {
                          if (date && date >= startDate) {
                            setEndDate(date)
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive">
            <Icon name="warningCircleFilled" className="h-4 w-4" />
            <AlertDescription>
              Failed to load service stats: {error instanceof Error ? error.message : String(error)}
            </AlertDescription>
          </Alert>
        )}

        {!error && isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}

        {!error && !isLoading && data?.performanceIssues && data.performanceIssues.length > 0 && (
          <div className="overflow-auto max-h-96">
            <table className="w-full border-separate border-spacing-0">
              <thead className="sticky top-0 z-10 bg-palette-jungleGreen000 border border-palette-jungleGreen200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-normal text-palette-jungleGreen900 whitespace-nowrap border-y border-palette-jungleGreen200 first:border-l first:rounded-tl-lg last:border-r last:rounded-tr-lg">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-normal text-palette-jungleGreen900 whitespace-nowrap border-y border-palette-jungleGreen200">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-normal text-palette-jungleGreen900 whitespace-nowrap border-y border-palette-jungleGreen200">
                    Type
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-normal text-palette-jungleGreen900 whitespace-nowrap border-y border-palette-jungleGreen200">
                    No Show
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-normal text-palette-jungleGreen900 whitespace-nowrap border-y border-palette-jungleGreen200">
                    Unnotified Absences
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-normal text-palette-jungleGreen900 whitespace-nowrap border-y border-palette-jungleGreen200">
                    Cancellations by Driver
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-normal text-palette-jungleGreen900 whitespace-nowrap border-y border-palette-jungleGreen200">
                    Lateness
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-normal text-palette-jungleGreen900 whitespace-nowrap border-y border-palette-jungleGreen200 last:border-r last:rounded-tr-lg">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.performanceIssues.map((entry) => (
                  <tr
                    key={entry.personId}
                    className="cursor-pointer border-b border-palette-jungleGreen000 transition-colors hover:bg-palette-jungleGreen000"
                    onClick={() => handlePersonClick(entry.personId)}
                  >
                    <td className="px-4 py-3 text-sm text-palette-jungleGreen700 whitespace-nowrap font-medium">
                      {formatPersonShortName({
                        person_id: entry.personId ?? "",
                        first_name: entry.firstName,
                        surname: entry.surname,
                      })}
                    </td>
                    <td className="px-4 py-3 text-sm text-palette-jungleGreen700 whitespace-nowrap">
                      {entry.location && entry.location.length > 0 ? (
                        <div className="flex gap-1 items-center">
                          <span>{entry.location.slice(0, 2).join(", ")}</span>
                          {entry.location.length > 2 && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-palette-jungleGreen400 cursor-default">
                                    +{entry.location.length - 2}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{entry.location.slice(2).join(", ")}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-palette-jungleGreen700 whitespace-nowrap">
                      {entry.sheriffType || "-"}
                    </td>
                    <td
                      className={cn(
                        "px-4 py-3 text-sm text-palette-jungleGreen700 whitespace-nowrap text-right",
                        { "font-bold": entry.awolCount > 0 },
                      )}
                    >
                      {entry.awolCount}
                    </td>
                    <td
                      className={cn(
                        "px-4 py-3 text-sm text-palette-jungleGreen700 whitespace-nowrap text-right",
                        { "font-bold": entry.unnotifiedAbsenceCount > 0 },
                      )}
                    >
                      {entry.unnotifiedAbsenceCount}
                    </td>
                    <td
                      className={cn(
                        "px-4 py-3 text-sm text-palette-jungleGreen700 whitespace-nowrap text-right",
                        { "font-bold": entry.driverCancellationCount > 0 },
                      )}
                    >
                      {entry.driverCancellationCount}
                    </td>
                    <td
                      className={cn(
                        "px-4 py-3 text-sm text-palette-jungleGreen700 whitespace-nowrap text-right",
                        { "font-bold": entry.latenessCount > 0 },
                      )}
                    >
                      {entry.latenessCount}
                    </td>
                    <td
                      className={cn(
                        "px-4 py-3 text-sm text-palette-jungleGreen700 whitespace-nowrap text-right",
                        { "font-bold": entry.total > 0 },
                      )}
                    >
                      {entry.total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!error &&
          !isLoading &&
          (!data?.performanceIssues || data.performanceIssues.length === 0) && (
            <div className="text-sm text-palette-jungleGreen500 text-center py-8">
              No driver service issues in this period
            </div>
          )}
      </CardContent>
    </Card>
  )
}
